import { combineLatest, from, map, Observable, of, switchMap, take } from 'rxjs';

import { Injectable } from '@angular/core';
import { collectionData, doc, docData, DocumentReference, getDocs, orderBy, query, where } from '@angular/fire/firestore';
import { IInventoryItem, IInventoryItemLocation, IInventoryLocation, IInventoryLocationItemWithData } from '@inventory/models/inventory';
import { FireStoreCollection } from '@shared/models/firestore';
import { FirestoreService } from '@shared/services/firestore/firestore.service';

@Injectable({
	providedIn: 'root',
})
export class InventoryItemsLocationsService extends FirestoreService<IInventoryItemLocation> {
	constructor() {
		super(FireStoreCollection.INVENTORY_ITEMS_LOCATIONS);
	}

	getInventoryItemLocationsForItem(
		organisationRef: DocumentReference,
		inventoryItemId: string,
	): Observable<Array<IInventoryItemLocation>> {
		const inventoryItemRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_ITEMS}/${inventoryItemId}`);
		let inventoryQuery = query(this.collectionRef, where('organisation', '==', organisationRef));
		inventoryQuery = query(inventoryQuery, where('inventoryItem', '==', inventoryItemRef));
		inventoryQuery = query(inventoryQuery, orderBy('createdDate', 'desc'));

		return from(getDocs(inventoryQuery)).pipe(
			map((querySnapshot) => querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as IInventoryItemLocation)),
		);
	}

	getInventoryItemLocationsWithDataForItem(
		organisationRef: DocumentReference,
		inventoryItemId: string,
	): Observable<Array<IInventoryLocationItemWithData>> {
		const inventoryItemRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_ITEMS}/${inventoryItemId}`);

		const inventoryQuery = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('inventoryItem', '==', inventoryItemRef),
			orderBy('createdDate', 'desc'),
		);

		return collectionData<IInventoryItemLocation>(inventoryQuery, { idField: 'id' }).pipe(
			switchMap((itemLocations) => {
				if (itemLocations.length === 0) {
					return of([]);
				}

				// Extract unique location refs
				const locationRefs = Array.from(new Set(itemLocations.map((i) => i.location).filter((r): r is DocumentReference => !!r)));

				// Only need ONE inventory item ref (we already know it)
				const inventoryItemData$ = docData<IInventoryItem>(inventoryItemRef as DocumentReference<IInventoryItem>, {
					idField: 'id',
				}).pipe(take(1));

				// Fetch all location docs
				const locationObservables = locationRefs.map((ref) =>
					docData<IInventoryLocation>(ref as DocumentReference<IInventoryLocation>, { idField: 'id' }).pipe(take(1)),
				);

				return combineLatest([inventoryItemData$, ...locationObservables]).pipe(
					map(([inventoryItemData, ...locations]) => {
						const locationMap = new Map(locationRefs.map((ref, i) => [ref.path, locations[i]]));

						return itemLocations.map((itemLoc) => ({
							...itemLoc,
							locationData: itemLoc.location ? locationMap.get(itemLoc.location.path) || null : null,
							inventoryItemData: inventoryItemData || null,
						}));
					}),
				);
			}),
		);
	}

	getInventoryItemLocationsWithDataForLocation(
		organisationRef: DocumentReference,
		locationId: string,
	): Observable<Array<IInventoryLocationItemWithData>> {
		const locationRef = doc(this.firestore, `${FireStoreCollection.INVENTORY_LOCATIONS}/${locationId}`);

		const inventoryQuery = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('location', '==', locationRef),
			orderBy('createdDate', 'desc'),
		);

		return collectionData<IInventoryItemLocation>(inventoryQuery, { idField: 'id' }).pipe(
			switchMap((itemLocations) => {
				if (itemLocations.length === 0) {
					return of([]);
				}

				// Extract unique inventory item refs
				const inventoryRefs = Array.from(
					new Set(itemLocations.map((i) => i.inventoryItem).filter((r): r is DocumentReference => !!r)),
				);

				// Load the single location document
				const locationData$ = docData<IInventoryLocation>(locationRef as DocumentReference<IInventoryLocation>, {
					idField: 'id',
				}).pipe(take(1));

				// Load all inventory item documents
				const inventoryItemObservables = inventoryRefs.map((ref) =>
					docData<IInventoryItem>(ref as DocumentReference<IInventoryItem>, {
						idField: 'id',
					}).pipe(take(1)),
				);

				return combineLatest([locationData$, ...inventoryItemObservables]).pipe(
					map(([locationData, ...inventoryItems]) => {
						const inventoryMap = new Map(inventoryRefs.map((ref, i) => [ref.path, inventoryItems[i]]));

						return itemLocations.map((itemLoc) => ({
							...itemLoc,
							locationData: locationData || null,
							inventoryItemData: itemLoc.inventoryItem ? inventoryMap.get(itemLoc.inventoryItem.path) || null : null,
						}));
					}),
				);
			}),
		);
	}
}
